import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useSupabaseQuery } from '../hooks/useSupabaseQuery';
import { useAuth } from '../hooks/useAuth';
import { isStaff } from '../lib/roles';
import { PageHeader } from '../components/ui/PageHeader';

const COP = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

interface DashboardMetrics {
  citasHoy: number;
  citasPendientes: number;
  citasAtendidasHoy: number;
  clientasActivas: number;
  ventasHoy: number;
  ventasMes: number;
  ventasServiciosMes: number;
  ventasProductosMes: number;
  comprasMes: number;
  gastosMes: number;
  pagosEmpleadosMes: number;
  productosStockBajo: number;
  productosAgotados: number;
  valorInventario: number;
}

function rangosDeTiempo() {
  const ahora = new Date();
  const inicioHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  const finHoy = new Date(inicioHoy);
  finHoy.setDate(finHoy.getDate() + 1);

  const inicioMes = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
  const finMes = new Date(ahora.getFullYear(), ahora.getMonth() + 1, 1);

  return {
    inicioHoy: inicioHoy.toISOString(),
    finHoy: finHoy.toISOString(),
    inicioMes: inicioMes.toISOString(),
    finMes: finMes.toISOString(),
    periodoActual: `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}`,
  };
}

/**
 * Dashboard (DAF v2, sección 26) — despacha por rol. Staff ve el resumen
 * operativo completo del salón (`DashboardStaff`, sin cambios de esta
 * ronda); una profesional ve una versión propia y acotada
 * (`DashboardProfesional`, pedido explícito del usuario: "ajústame el
 * Dashboard para el profesional, que sea de acuerdo") — nunca las cifras
 * financieras del salón completo, que además ni podría ver: `ventas`,
 * `comisiones` y `pagos_empleados` ya la limitan por RLS a sus propios
 * datos, y `compras`/`gastos`/`productos` (para valor de inventario) son
 * exclusivos de staff. Mostrarle esas secciones tal cual habría dado
 * $0 en casi todo, la impresión falsa de que el salón no vende nada.
 */
export function Dashboard() {
  const { perfil } = useAuth();
  return isStaff(perfil?.rol) ? <DashboardStaff /> : <DashboardProfesional />;
}

function DashboardStaff() {
  const rangos = useMemo(rangosDeTiempo, []);

  const { data, loading, error } = useSupabaseQuery<DashboardMetrics>(async () => {
    const { inicioHoy, finHoy, inicioMes, finMes, periodoActual } = rangos;

    const [
      citasHoyRes,
      pendientesRes,
      atendidasRes,
      clientasRes,
      ventasHoyRes,
      ventasMesRes,
      comprasMesRes,
      gastosMesRes,
      pagosEmpMesRes,
      productosRes,
    ] = await Promise.all([
      supabase.from('citas').select('id', { count: 'exact', head: true }).gte('inicio', inicioHoy).lt('inicio', finHoy),
      supabase.from('citas').select('id', { count: 'exact', head: true }).eq('estado', 'pendiente'),
      supabase
        .from('citas')
        .select('id', { count: 'exact', head: true })
        .eq('estado', 'atendida')
        .gte('inicio', inicioHoy)
        .lt('inicio', finHoy),
      supabase.from('clientes').select('id', { count: 'exact', head: true }).eq('estado', 'activo'),
      supabase.from('ventas').select('total').eq('estado', 'confirmada').gte('fecha', inicioHoy).lt('fecha', finHoy),
      supabase
        .from('ventas')
        .select('total, venta_detalle ( tipo, subtotal )')
        .eq('estado', 'confirmada')
        .gte('fecha', inicioMes)
        .lt('fecha', finMes),
      supabase.from('compras').select('total').eq('estado', 'confirmada').gte('fecha', inicioMes).lt('fecha', finMes),
      supabase.from('gastos').select('valor').eq('estado', 'activo').gte('fecha', inicioMes).lt('fecha', finMes),
      supabase.from('pagos_empleados').select('monto').eq('estado', 'pagado').eq('periodo', periodoActual),
      supabase.from('productos').select('stock_actual, stock_minimo, costo').eq('estado', 'activo'),
    ]);

    const resultados = [
      citasHoyRes,
      pendientesRes,
      atendidasRes,
      clientasRes,
      ventasHoyRes,
      ventasMesRes,
      comprasMesRes,
      gastosMesRes,
      pagosEmpMesRes,
      productosRes,
    ];
    const firstError = resultados.find((r) => r.error)?.error;
    if (firstError) return { data: null, error: firstError };

    const ventasHoy = (ventasHoyRes.data ?? []).reduce((s, v) => s + v.total, 0);

    const ventasMesFilas = (ventasMesRes.data ?? []) as unknown as Array<{
      total: number;
      venta_detalle: { tipo: 'servicio' | 'producto'; subtotal: number }[];
    }>;
    const ventasMes = ventasMesFilas.reduce((s, v) => s + v.total, 0);
    let ventasServiciosMes = 0;
    let ventasProductosMes = 0;
    for (const v of ventasMesFilas) {
      for (const d of v.venta_detalle ?? []) {
        if (d.tipo === 'servicio') ventasServiciosMes += d.subtotal;
        else ventasProductosMes += d.subtotal;
      }
    }

    const comprasMes = (comprasMesRes.data ?? []).reduce((s, c) => s + c.total, 0);
    const gastosMes = (gastosMesRes.data ?? []).reduce((s, g) => s + g.valor, 0);
    const pagosEmpleadosMes = (pagosEmpMesRes.data ?? []).reduce((s, p) => s + p.monto, 0);

    const productos = productosRes.data ?? [];
    const productosStockBajo = productos.filter((p) => p.stock_actual > 0 && p.stock_actual <= p.stock_minimo).length;
    const productosAgotados = productos.filter((p) => p.stock_actual <= 0).length;
    const valorInventario = productos.reduce((s, p) => s + p.costo * p.stock_actual, 0);

    return {
      data: {
        citasHoy: citasHoyRes.count ?? 0,
        citasPendientes: pendientesRes.count ?? 0,
        citasAtendidasHoy: atendidasRes.count ?? 0,
        clientasActivas: clientasRes.count ?? 0,
        ventasHoy,
        ventasMes,
        ventasServiciosMes,
        ventasProductosMes,
        comprasMes,
        gastosMes,
        pagosEmpleadosMes,
        productosStockBajo,
        productosAgotados,
        valorInventario,
      },
      error: null,
    };
  }, [rangos]);

  const resultadoOperativo = data
    ? data.ventasMes - data.comprasMes - data.gastosMes - data.pagosEmpleadosMes
    : 0;

  return (
    <div>
      <PageHeader title="Dashboard" description="Resumen operativo — DAF v2, sección 26" />

      {loading && <p className="text-sm text-onyx/50">Cargando métricas…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {data && (
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-onyx/40">Agenda</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard label="Citas de hoy" value={data.citasHoy} />
              <MetricCard label="Citas pendientes" value={data.citasPendientes} />
              <MetricCard label="Atendidas hoy" value={data.citasAtendidasHoy} />
              <MetricCard label="Clientas activas" value={data.clientasActivas} />
            </div>
          </div>

          <div>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-onyx/40">Ventas</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard label="Ventas de hoy" value={COP.format(data.ventasHoy)} />
              <MetricCard label="Ventas del mes" value={COP.format(data.ventasMes)} />
              <MetricCard label="— en servicios" value={COP.format(data.ventasServiciosMes)} muted />
              <MetricCard label="— en productos" value={COP.format(data.ventasProductosMes)} muted />
            </div>
          </div>

          <div>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-onyx/40">Inventario</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard label="Valor del inventario" value={COP.format(data.valorInventario)} />
              <MetricCard
                label="Stock bajo"
                value={data.productosStockBajo}
                alerta={data.productosStockBajo > 0}
                to="/admin/inventario"
              />
              <MetricCard
                label="Agotados"
                value={data.productosAgotados}
                alerta={data.productosAgotados > 0}
                to="/admin/inventario"
              />
            </div>
          </div>

          <div>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-onyx/40">Resultado del mes</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard label="Compras del mes" value={COP.format(data.comprasMes)} muted />
              <MetricCard label="Gastos del mes" value={COP.format(data.gastosMes)} muted />
              <MetricCard label="Pagos a empleados" value={COP.format(data.pagosEmpleadosMes)} muted />
              <MetricCard
                label="Resultado operativo"
                value={COP.format(resultadoOperativo)}
                clase={resultadoOperativo >= 0 ? 'text-green-700' : 'text-red-700'}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface DashboardProfesionalMetrics {
  citasHoy: number;
  citasPendientes: number;
  citasAtendidasHoy: number;
  ventasHoy: number;
  ventasMes: number;
  comisionMes: number;
}

/**
 * Dashboard de una profesional: solo su propia agenda del día, sus
 * propias ventas y su comisión del período — cada consulta ya viene
 * acotada por RLS a lo suyo (`citas_profesional_select`,
 * `ventas_profesional_select_propias`, `comisiones_profesional_select`),
 * así que no hace falta filtrar por `profesionalId` del lado del
 * cliente. Si todavía no tiene un esquema de remuneración por comisión
 * asignado, esa tarjeta simplemente muestra $0 — no es un error.
 */
function DashboardProfesional() {
  const rangos = useMemo(rangosDeTiempo, []);

  const { data, loading, error } = useSupabaseQuery<DashboardProfesionalMetrics>(async () => {
    const { inicioHoy, finHoy, inicioMes, finMes, periodoActual } = rangos;

    const [citasHoyRes, pendientesRes, atendidasRes, ventasHoyRes, ventasMesRes, comisionMesRes] = await Promise.all([
      supabase.from('citas').select('id', { count: 'exact', head: true }).gte('inicio', inicioHoy).lt('inicio', finHoy),
      supabase.from('citas').select('id', { count: 'exact', head: true }).eq('estado', 'pendiente'),
      supabase
        .from('citas')
        .select('id', { count: 'exact', head: true })
        .eq('estado', 'atendida')
        .gte('inicio', inicioHoy)
        .lt('inicio', finHoy),
      supabase.from('ventas').select('total').eq('estado', 'confirmada').gte('fecha', inicioHoy).lt('fecha', finHoy),
      supabase.from('ventas').select('total').eq('estado', 'confirmada').gte('fecha', inicioMes).lt('fecha', finMes),
      supabase.from('comisiones').select('monto_comision').eq('periodo', periodoActual).eq('estado', 'activo'),
    ]);

    const resultados = [citasHoyRes, pendientesRes, atendidasRes, ventasHoyRes, ventasMesRes, comisionMesRes];
    const firstError = resultados.find((r) => r.error)?.error;
    if (firstError) return { data: null, error: firstError };

    const ventasHoy = (ventasHoyRes.data ?? []).reduce((s, v) => s + v.total, 0);
    const ventasMes = (ventasMesRes.data ?? []).reduce((s, v) => s + v.total, 0);
    const comisionMes = (comisionMesRes.data ?? []).reduce((s, c) => s + c.monto_comision, 0);

    return {
      data: {
        citasHoy: citasHoyRes.count ?? 0,
        citasPendientes: pendientesRes.count ?? 0,
        citasAtendidasHoy: atendidasRes.count ?? 0,
        ventasHoy,
        ventasMes,
        comisionMes,
      },
      error: null,
    };
  }, [rangos]);

  return (
    <div>
      <PageHeader title="Dashboard" description="Tu resumen del día" />

      {loading && <p className="text-sm text-onyx/50">Cargando métricas…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {data && (
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-onyx/40">Mi agenda</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <MetricCard label="Citas de hoy" value={data.citasHoy} to="/admin/agenda" />
              <MetricCard label="Citas pendientes" value={data.citasPendientes} to="/admin/agenda" />
              <MetricCard label="Atendidas hoy" value={data.citasAtendidasHoy} to="/admin/agenda" />
            </div>
          </div>

          <div>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-onyx/40">Mis ventas</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <MetricCard label="Ventas de hoy" value={COP.format(data.ventasHoy)} to="/admin/ventas" />
              <MetricCard label="Ventas del mes" value={COP.format(data.ventasMes)} to="/admin/ventas" />
              <MetricCard label="Mi comisión del mes" value={COP.format(data.comisionMes)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({
  label,
  value,
  muted,
  alerta,
  clase,
  to,
}: {
  label: string;
  value: number | string;
  muted?: boolean;
  alerta?: boolean;
  clase?: string;
  to?: string;
}) {
  const contenido = (
    <div className={`card ${alerta ? 'border-amber-300 bg-amber-50' : ''}`}>
      <p className="text-sm text-onyx/50">{label}</p>
      <p
        className={`mt-2 font-display ${muted ? 'text-xl text-onyx/70' : 'text-3xl'} ${
          clase ?? (alerta ? 'text-amber-700' : 'text-gold-deep')
        }`}
      >
        {value}
      </p>
    </div>
  );

  return to ? (
    <Link to={to} className="block transition-opacity hover:opacity-80">
      {contenido}
    </Link>
  ) : (
    contenido
  );
}
